/**
 * Singleton WebSocket manager for Girder notifications
 * Manages a single WebSocket connection outside React's component lifecycle
 * to prevent duplicate connections and simplify state management
 */
class GirderWebSocket {
  constructor() {
    this.ws = null;
    this.token = null;
    this.girderUrl = null;
    this.handlers = new Map();
    this.reconnectTimeout = null;
    this.reconnectAttempts = 0;
    this.connectionState = 'disconnected'; // 'disconnected', 'connecting', 'connected', 'error'
    this.shouldConnect = false;
    this.connectionStateListeners = new Set();
  }

  /**
   * Get WebSocket URL from token and optional girderUrl
   */
  getWebSocketUrl(token, girderUrl = null) {
    if (!token) {
      return null;
    }
    
    // Determine base URL
    let baseUrl = girderUrl;
    if (!baseUrl) {
      // Use current window location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      baseUrl = `${protocol}//${host}`;
    } else {
      // Convert http/https to ws/wss
      baseUrl = baseUrl.replace(/^http/, 'ws');
    }
    
    // Construct notification endpoint
    const apiBase = '/dsa'; // Match API.js endpoint base (/dsa/api/v1)
    return `${baseUrl}${apiBase}/notifications/me?token=${encodeURIComponent(token)}`;
  }

  /**
   * Update connection state and notify listeners
   */
  setConnectionState(newState) {
    if (this.connectionState !== newState) {
      this.connectionState = newState;
      // Notify all state listeners
      this.connectionStateListeners.forEach(listener => {
        try {
          listener(newState);
        } catch (error) {
          console.error('[GirderWebSocket] Error in state listener:', error);
        }
      });
    }
  }

  /**
   * Connect to WebSocket with token
   */
  connect(token, girderUrl = null) {
    // If already connected/connecting with same token, do nothing
    if (this.ws) {
      const state = this.ws.readyState;
      if ((state === WebSocket.OPEN || state === WebSocket.CONNECTING) && 
          this.token === token && 
          this.girderUrl === girderUrl) {
        return; // Already connected/connecting with same credentials
      }
      // If token/URL changed or connection is closing/closed, disconnect first
      if (this.token !== token || this.girderUrl !== girderUrl) {
        this.disconnect();
      } else if (state === WebSocket.CLOSING || state === WebSocket.CLOSED) {
        // Clear the old connection reference
        this.ws = null;
      }
    }

    this.token = token;
    this.girderUrl = girderUrl;
    this.shouldConnect = true;

    const url = this.getWebSocketUrl(token, girderUrl);
    if (!url) {
      this.setConnectionState('error');
      return;
    }

    this.setConnectionState('connecting');

    try {
      const ws = new WebSocket(url);
      this.ws = ws;

      ws.onopen = () => {
        console.log('[GirderWebSocket] Connected to Girder notifications');
        this.setConnectionState('connected');
        this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          console.log('[WebSocket] Received notification:', notification);
          
          // Call all registered handlers
          this.handlers.forEach((handler) => {
            try {
              handler(notification);
            } catch (error) {
              console.error('[GirderWebSocket] Error in notification handler:', error);
            }
          });
        } catch (error) {
          console.error('[GirderWebSocket] Failed to parse notification:', error, event.data);
        }
      };

      ws.onerror = (error) => {
        console.error('[GirderWebSocket] Connection error:', error);
        this.setConnectionState('error');
      };

      ws.onclose = (event) => {
        console.log('[GirderWebSocket] Connection closed', event.code, event.reason);
        this.setConnectionState('disconnected');
        this.ws = null;

        // Auto-reconnect if we should be connected and it wasn't a clean close
        if (this.shouldConnect && event.code !== 1000) {
          // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
          this.reconnectAttempts += 1;
          
          console.log(`[GirderWebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
          
          this.reconnectTimeout = setTimeout(() => {
            if (this.shouldConnect && this.token) {
              this.connect(this.token, this.girderUrl);
            }
          }, delay);
        }
      };
    } catch (error) {
      console.error('[GirderWebSocket] Failed to create connection:', error);
      this.setConnectionState('error');
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    this.shouldConnect = false;
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      const ws = this.ws;
      // Remove event handlers to prevent reconnection attempts
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      // Close the connection
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close(1000, 'Client disconnecting');
      }
      this.ws = null;
    }
    
    this.setConnectionState('disconnected');
    this.reconnectAttempts = 0;
    this.token = null;
    this.girderUrl = null;
  }

  /**
   * Subscribe to WebSocket messages
   * @param {Function} handler - Function to call when a message is received
   * @returns {Function} Unsubscribe function
   */
  subscribe(handler) {
    const id = Symbol('handler');
    this.handlers.set(id, handler);
    
    // Return unsubscribe function
    return () => {
      this.handlers.delete(id);
    };
  }

  /**
   * Subscribe to connection state changes
   * @param {Function} listener - Function to call when state changes
   * @returns {Function} Unsubscribe function
   */
  onStateChange(listener) {
    this.connectionStateListeners.add(listener);
    // Immediately call with current state
    try {
      listener(this.connectionState);
    } catch (error) {
      console.error('[GirderWebSocket] Error in state listener:', error);
    }
    
    // Return unsubscribe function
    return () => {
      this.connectionStateListeners.delete(listener);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionState() {
    return this.connectionState;
  }

  /**
   * Check if currently connected
   */
  isConnected() {
    return this.connectionState === 'connected';
  }
}

// Create and export singleton instance
export const girderWebSocket = new GirderWebSocket();
